// supabaseClient.ts — the single configured supabase-js client for the app.
//
// Session persistence uses our defensive storage.ts (localStorage in the
// webview, in-memory fallback) so a private-mode/quota throw can't crash auth.
// Sign-in is passwordless email OTP only: verifyOtp returns the session directly,
// so we use the default (implicit) flow and do NOT parse sessions from the URL.
// (PKCE + detectSessionInUrl are for the OAuth / magic-link code-exchange flow,
// which we don't use — leaving them on caused stray `flow_state_not_found` errors
// when a stale `?code=` was in the URL.)
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { SUPABASE } from '@/config/supabase';
import { storage } from './storage';

const storageAdapter = {
  getItem: (key: string) => storage.get(key),
  setItem: (key: string, value: string) => storage.set(key, value),
  removeItem: (key: string) => storage.remove(key),
};

let client: SupabaseClient | null = null;

/** Lazily build the client. Returns null when Supabase isn't configured. */
export function supabase(): SupabaseClient | null {
  if (!SUPABASE.url || !SUPABASE.anonKey) return null;
  if (!client) {
    client = createClient(SUPABASE.url, SUPABASE.anonKey, {
      auth: {
        storage: storageAdapter,
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
      },
    });
  }
  return client;
}
